import React, { useEffect, useState } from 'react';
import { ReactComponent as PartsNotFoundImage } from 'assets/images/parts-not-found.svg';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import { useSearchParams } from 'react-router-dom';
import CatalogNavbar from 'components/navbar/catalog-navbar/CatalogNavbar';
import ProductsViewMode from 'components/ProductsViewMode/ProductsViewMode';
import { DEFAULT_VIEW } from 'components/ProductsViewMode/ProductsViewMode.constants';
import ProductsViewModeChanger from 'components/ProductsViewModeChanger/ProductsViewModeChanger';
import List from 'components/List/List';
import { ICategory } from 'views/Sidebar/Sidebar.model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowLeft } from '@fortawesome/free-solid-svg-icons';
import SortBy, { SortByParams } from 'components/SortBy/SortBy';
import { sortByOptions } from 'components/SortBy/SortBy.map';
import CatalogTyresFilter from '../CatalogTyresFilter/CatalogTyresFilter';
import { IBodyResponseTyre, ITyreFilter } from 'interfaces/tyre';
import { CATALOG_TYRE } from 'components/ProductsViewModeChanger/products-view-mode-changer-utils';
import { getTranslationName } from 'utils/locale';
import './CatalogTyresMobile.scss';
import useReliableCatalogCarQueryParams from 'utils/hooks/useReliableCatalogCarQueryParams';

const CatalogTyresMobile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const http = useHttp();
  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState<IBodyResponseTyre>();
  const [filterData, setFilterData] = useState<ITyreFilter>();
  const [openFilter, setOpenFilter] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')));
  const [tyreCategories, setTyreCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isCategorySelected, setIsCategorySelected] = useState(false);
  const user = JSON.parse(localStorage.getItem('userData')!);
  const [viewMode, setViewMode] = useState(user?.user_settings.products_view_mode || DEFAULT_VIEW);
  const [activeCategoryInfo, setActiveCategoryInfo] = useState<ICategory>();
  const [totalCount, setTotalCount] = useState(0);
  const reliableSearchParams = useReliableCatalogCarQueryParams();

  function getTyreCategories() {
    setLoadingCategories(true);
    http.catalogs.getTyreAllCategories({}).then((res) => {
      setTyreCategories(res);
      setTotalCount(res.reduce((total, item) => total + Number(item.number_of_products), 0));
    }).finally(() => setLoadingCategories(false));
  }

  function getTyresData(data) {
    setLoading(true);

    const sortBySelectedOption = sortByOptions.filter((item) => String(item.value) == String(searchParams.get('sort')))[0] ?? [];

    const sortParams: SortByParams = {
      sort_by_column: String(searchParams.get('sort') ?? ''),
      sort_by_asc: String(searchParams.get('asc') ?? sortBySelectedOption?.default_sort_asc ?? ''),
    };

    const params = {
      ...data,
      ...sortParams,
      page,
    };

    const validParams = validateParams(params);

    http.tyreHttp
      .getTyresProducts(validParams)
      .then((res) => {
        setRefData(res.data);
      })
      .finally(() => setLoading(false));
  }

  function getTyresFilter(params) {
    setFilterData(undefined);
    const filterParams = {
      ...params,
      page,
    };

    const validFilterParams = validateParams(filterParams);

    http.tyreHttp.getTyresFilters(validFilterParams).then((res) => {
      setFilterData(res.data);
    });
  }

  const validateParams = (data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});
  };

  const handlePageClick = (pageN: number) => {
    setPage(pageN);
  };


  const onCategoryClick = (category) => {
    setActiveCategoryInfo(category);
    setSearchParams({ category_id: [category.id] });
    setPage(1);
    setIsCategorySelected(true);
  };

  useEffect(() => {
    setRefData(undefined);

    const params = {
      season_ids: searchParams.get('category_id'),
      brand_ids: searchParams.getAll('brand_ids'),
      widths: searchParams.getAll('widths'),
      heights: searchParams.getAll('heights'),
      load_indexes: searchParams.get('load_indexes'),
      speed_ratings: searchParams.get('speed_ratings'),
      page: searchParams.get('page'),
      q: searchParams.get('q')
    };

    if (searchParams.get('category_id')) {
      getTyresData(params);

    }
  }, [
    page,
    searchParams.get('category_id'),
    searchParams.get('brand_ids'),
    searchParams.get('widths'),
    searchParams.get('heights'), searchParams.get('load_indexes'),
    searchParams.get('speed_ratings'),
    searchParams.get('sort'),
    searchParams.get('asc'),
    tyreCategories.length
  ]);


  useEffect(() => {


    const params = {
      season_ids: searchParams.get('category_id'),
      brand_ids: searchParams.getAll('brand_ids'),
      widths: searchParams.getAll('widths'),
      heights: searchParams.getAll('heights'),
      load_indexes: searchParams.get('load_indexes'),
      speed_ratings: searchParams.get('speed_ratings'),
      page: searchParams.get('page'),
      q: searchParams.get('q')
    };

    if (searchParams.get('category_id')) {
      getTyresFilter(params);
    }
  }, [searchParams.get('category_id')]);
  useEffect(() => {
    getTyreCategories();
  }, []);


  function filterOptionsElement() {
    if (!searchParams.get('category_id')) {
      return <div className="catalog-parts-filter"></div>;
    }

    return (
      <div className="catalog-parts-filter">
        <button onClick={() => setOpenFilter(true)} className={`catalog-parts-button ${openFilter ? 'active' : ''}`}>
          {t('_filter_results_btn')}
        </button>
      </div>
    );
  }


  const onClickReturn = () => {

    setSearchParams({ ...reliableSearchParams });
    setIsCategorySelected(false);
  };

  return (
    <div className="view-catalog tyres-catalog-wrapper-mobile">
      <CatalogNavbar option={filterOptionsElement()} title="_tyre_catalog" />

      <div className="filterOptions">
        {refData?.data?.length && refData?.data?.length > 0 && searchParams.get('category_id') ? <SortBy /> : null}
        <div className="results">
          {!isCategorySelected && totalCount ? totalCount + ' ' + t('_results') : (refData?.data?.length && refData?.data?.length > 0 ? refData?.total_count + ' ' + t('_results') : (loading ? <Spinner class="extra-small" /> : null))}
        </div>
        <ProductsViewMode viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      {isCategorySelected ? (
        <div className="tyres-catalog-heading">
          <div className="tyres-category-return" onClick={() => onClickReturn()}><FontAwesomeIcon icon={faCircleArrowLeft} />{t('_return')}</div>
          <div className="tyres-category-title">{activeCategoryInfo?.name}</div>
        </div>
      ) : null}

      <div className="retailer-catalog-tyres-container-mobile">
        {!isCategorySelected ? (
          <div className={`catalog-tyres-sidebar-mobile ${loadingCategories ? 'loading' : ''}`}>
            {loadingCategories ? <Spinner class="small" /> : (
              tyreCategories && tyreCategories.length ? (
                <List
                  content={tyreCategories?.map((item) => {
                    return { ...item, id: item.id, name: getTranslationName(item.name) };
                  })}
                  images={tyreCategories?.map((item) => item.image)}
                  itemClassName="parts-list-catalog-item"
                  itemId="id"
                  onItemClick={onCategoryClick}
                //  activeCategory={activeCategory}
                />
              ) : null
            )}
          </div>
        ) : (
          <div className="wrapperItems">
            {loading ? (
              <Spinner class="car-parts-spinner" />
            ) : refData?.data?.length ? (
              <ProductsViewModeChanger catalog={CATALOG_TYRE} viewMode={viewMode} data={refData?.data} />
            ) : (
              <div className="no-parts-data">
                <div className="notFoundSvg">
                  <PartsNotFoundImage />
                </div>
                <h1>{t('_tyres_not_found')}</h1>
              </div>
            )}
          </div>
        )}
      </div>
      {(refData && refData?.total_pages && refData?.page && refData?.total_pages > 1) && isCategorySelected ? (
        <Pagination pageCount={refData.total_pages} handlePageClick={handlePageClick} forcePage={refData.page} />
      ) : (
        null
      )}
      <CatalogTyresFilter getData={getTyresData} filterData={filterData} openFilter={openFilter} setOpenFilter={setOpenFilter} />
    </div>
  );
};

export default CatalogTyresMobile;
